import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies -- installed with react; imported by the other shared components too
import PropTypes from 'prop-types';
import clsx from 'clsx';

const CIRCLE_CLASS =
	'inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-xs font-bold leading-none text-white';

/**
 * A red filled circle with a white "i" and a popover of plain text: why an AI run waits (paused, or queued behind the
 * OpenAI hold). The text is server data, so it reaches the page only as a React text node or a title attribute, never as HTML.
 * Without children the popover is the browser's own title (like the yellow "!" of OpenAiSpendDisplay); with children
 * (a button, a list) a CSS panel shows on hover and on keyboard focus, with no state and no timer, and `open` keeps it
 * visible. The cursor is the default one everywhere, also over the panel.
 * @param {string} text The popover text
 * @param {React.ReactNode} [children] More popover content below the text (needs the panel)
 * @param {boolean} [open] Holds the panel visible (only with children)
 * @param {string} [className] Extra classes of the outer span
 */
const InfoIconRed = ({ text, children = null, open = false, className }) => {
	if (!children) {
		return (
			<span className={clsx('cursor-default', className)} title={text} aria-label={text} role="img">
				<span className={CIRCLE_CLASS}>i</span>
			</span>
		);
	}
	return (
		<span
			className={clsx('group relative inline-flex cursor-default', className)}
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard focus opens the CSS popover (group-focus-within)
			tabIndex={0}
			role="img"
			aria-label={text}
		>
			<span className={CIRCLE_CLASS}>i</span>
			<span
				className={clsx(
					'absolute left-0 top-full z-20 pt-1',
					open ? 'visible' : 'invisible group-hover:visible group-focus-within:visible',
				)}
			>
				<span className="flex w-72 cursor-default flex-col gap-2 whitespace-normal break-words rounded bg-gray-800 p-2 text-left text-xs font-normal text-gray-200 shadow-lg">
					<span>{text}</span>
					{children}
				</span>
			</span>
		</span>
	);
};

InfoIconRed.propTypes = {
	text: PropTypes.string.isRequired,
	// eslint-disable-next-line react/require-default-props -- optional, defaulted in the signature; React 18.3 deprecates defaultProps on function components
	children: PropTypes.node,
	// eslint-disable-next-line react/require-default-props -- optional, defaulted in the signature; React 18.3 deprecates defaultProps on function components
	open: PropTypes.bool,
	// eslint-disable-next-line react/require-default-props -- optional, read from props; React 18.3 deprecates defaultProps on function components
	className: PropTypes.string,
};

export default InfoIconRed;
